import React, { useState, useEffect, useCallback, useMemo } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { Text, Box } from "theme-ui";
import CopySimpleIcon from "../assets/images/copy_simple.svg"
import { LinkExplorer } from "./PSY/LinkExplorer";
import { CollateralOption } from "./PSY/CollateralOption";

import {
  Percent,
  MINIMUM_COLLATERAL_RATIO,
  UserTrove,
  Decimal
} from "@liquity/lib-base";
import { BlockPolledLiquityStoreState, getContractAddressByCollateralKey } from "@liquity/lib-ethers";
import { useLiquitySelector } from "@liquity/lib-react";
import { shortenAddress } from "../utils/shortenAddress";
import { useLiquity } from "../hooks/LiquityContext";
import { COIN } from "../strings";
import { CollateralLexicons } from "../lexicon";

import { Icon } from "./Icon";
import { LoadingOverlay } from "./LoadingOverlay";
import { Transaction } from "./Transaction";
import { Tooltip } from "./Tooltip";
import { Abbreviation } from "./Abbreviation";
import { getCollateralRatioStyle } from '../utils/helper'
import Select from 'react-select'

const rowHeight = "40px";
const options = CollateralLexicons.map(({ key, term, iconUrl }) => ({ value: key, label: term, iconUrl }))

const liquidatableInNormalMode = (trove: UserTrove, price: Decimal) =>
  [trove.collateralRatioIsBelowMinimum(price), "Collateral ratio is not low enough"] as const;

const liquidatableInRecoveryMode = (
  trove: UserTrove,
  price: Decimal,
  totalCollateralRatio: Decimal,
  slsdInStabilityPool: Decimal
) => {
  const collateralRatio = trove.collateralRatio(price);

  if (collateralRatio.gte(MINIMUM_COLLATERAL_RATIO) && collateralRatio.lt(totalCollateralRatio)) {
    return [
      trove.debt.lte(slsdInStabilityPool),
      "There's not enough SLSD in the Stability pool to cover the debt"
    ] as const;
  } else {
    return liquidatableInNormalMode(trove, price);
  }
};

type RiskyTrovesProps = {
  pageSize: number;
};

type MessageProps = {
  troves: UserTrove[] | undefined
}

const Message: React.FC<MessageProps> = ({ troves }) => {
  return (
    <div>
      <div className="text-center text-style-value">
        {!troves ? "Loading..." : "There are no Troves yet"}
      </div>
    </div>
  )
}

export const RiskyTroves: React.FC<RiskyTrovesProps> = ({ pageSize }) => {
  const [collateral, setCollateral] = useState(options[0]);
  const { liquity } = useLiquity();

  const select = useCallback(({ collateralStates, blockTag}: BlockPolledLiquityStoreState) => {
    const { price, slsdInStabilityPool, total, numberOfTroves } = collateralStates[collateral.value];

    return {
      numberOfTroves,
      price,
      recoveryMode: total.collateralRatioIsBelowCritical(price),
      totalCollateralRatio: total.collateralRatio(price),
      slsdInStabilityPool,
      blockTag
    }
  }, [collateral]);

  const { assetAddress } = useMemo(() => {
    return getContractAddressByCollateralKey(liquity.connection, collateral.value)
  }, [liquity.connection, collateral.value])

  const {
    blockTag,
    numberOfTroves,
    recoveryMode,
    totalCollateralRatio,
    slsdInStabilityPool,
    price
  } = useLiquitySelector(select);

  const [loading, setLoading] = useState(true);
  const [troves, setTroves] = useState<UserTrove[]>();

  const [reload, setReload] = useState({});
  const forceReload = useCallback(() => setReload({}), []);

  const [page, setPage] = useState(0);
  const numberOfPages = Math.ceil(numberOfTroves / pageSize) || 1;
  const clampedPage = Math.min(page, numberOfPages - 1);

  const chainId = liquity.connection.chainId
  const nextPage = () => {
    if (clampedPage < numberOfPages - 1) {
      setPage(clampedPage + 1);
    }
  };

  const previousPage = () => {
    if (clampedPage > 0) {
      setPage(clampedPage - 1);
    }
  };

  useEffect(() => {
    if (page !== clampedPage) {
      setPage(clampedPage);
    }
  }, [page, clampedPage]);

  useEffect(() => {
    let mounted = true;

    setLoading(true);

    if (!!assetAddress) {
      liquity
        .getTroves(
          assetAddress,
          {
            first: pageSize,
            sortedBy: "ascendingCollateralRatio",
            startingAt: clampedPage * pageSize
          },
          { blockTag }
        )
        .then(troves => {
          if (mounted) {
            setTroves(troves);
            setLoading(false);
          }
        });
    }

    return () => {
      mounted = false;
    };
    // Omit blockTag from deps on purpose
    // eslint-disable-next-line
  }, [liquity, clampedPage, pageSize, reload, collateral]);

  useEffect(() => {
    forceReload();
  }, [forceReload, numberOfTroves]);

  const [copied, setCopied] = useState<string>();

  useEffect(() => {
    if (copied !== undefined) {
      let cancelled = false;

      setTimeout(() => {
        if (!cancelled) {
          setCopied(undefined);
        }
      }, 2000);

      return () => {
        cancelled = true;
      };
    }
  }, [copied]);

  return (
    <>
      <Select
        options={options}
        defaultValue={collateral}
        formatOptionLabel={(option) => (
          <CollateralOption {...option} />
        )}
        onChange={(option) => {
          const val = Object.assign({}, option)
          setCollateral(val)
        }}
        className="react-select-container mt-8"
        classNamePrefix="react-select"
        // defaultMenuIsOpen={true}
      />
      <div className="mt-8">
        {!troves || troves.length === 0 || !assetAddress ? (
          <Message troves={troves} />
        ) : (
          <div className="bg-component border-component">
            <div className="w-full list col-list md:list-table">
              {/* <colgroup>
                <col style={{ width: "50px" }} />
                <col />
                <col />
                <col />
                <col style={{ width: rowHeight }} />
              </colgroup> */}

              <div className="thead hidden">
                <div className="tr">
                  <div className="th">Owner</div>
                  <div className="th">
                    <Abbreviation short="Coll.">Collateral</Abbreviation>
                    <span className="text-style-emphasize ml-2">{collateral.label}</span>
                  </div>
                  <div className="th">
                    Debt
                    <span className="text-style-emphasize ml-2">{COIN}</span>
                  </div>
                  <div className="th">
                    Collateral Ratio
                  </div>
                  <div className="th"></div>
                </div>
              </div>

              <div className="tbody">
                {troves.map(
                  trove => {
                    const liquidatable = recoveryMode
                      ? liquidatableInRecoveryMode(
                          trove,
                          price,
                          totalCollateralRatio,
                          slsdInStabilityPool
                        )
                      : liquidatableInNormalMode(trove, price)
                    const [isLiquidatable, _reason] = liquidatable;

                    return !trove.isEmpty && ( // making sure the Trove hasn't been liquidated
                      // (WONT-FIX: remove check after we can fetch multiple Troves in one call)
                      <div className="tr relative p-4 md:p-0" key={trove.ownerAddress}>
                        <div className="td">
                          <div className="flex flex-row items-center">
                            <Tooltip message={trove.ownerAddress} placement="top">
                              <Text
                                variant="address"
                                sx={{
                                  width: ["73px", "unset"],
                                  overflow: "hidden",
                                  position: "relative"
                                }}
                              >
                                {shortenAddress(trove.ownerAddress)}
                                <Box
                                  sx={{
                                    display: ["block", "none"],
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    width: "50px",
                                    height: "100%",
                                  }}
                                />
                              </Text>
                            </Tooltip>

                            <div className="ml-4 flex items-cener">
                              <CopyToClipboard
                                text={trove.ownerAddress}
                                onCopy={() => setCopied(trove.ownerAddress)}
                              >
                                <button className="w-6 h-6">
                                  <img src={CopySimpleIcon} alt="copy to clipboard"/>
                                </button>
                              </CopyToClipboard>
                            </div>
                            <LinkExplorer chainId={chainId} address={trove.ownerAddress} />
                          </div>
                        </div>
                        <div className="td flex flex-row justify-between mt-3 md:mt-0">
                          <div className="text-style-label md:hidden">
                            Collateral
                            <span className="text-style-mob-emphasize ml-1.5">{collateral.label}</span>
                          </div>
                          <Abbreviation short={trove.collateral.shorten()}>
                            {trove.collateral.prettify(4)}
                          </Abbreviation>
                        </div>
                        <div className="td flex flex-row justify-between mt-2 md:mt-0">
                          <div className="text-style-label md:hidden">
                            Debt
                            <span className="text-style-mob-emphasize ml-1.5">{COIN}</span>
                          </div>
                          <Abbreviation short={trove.debt.shorten()}>
                            {trove.debt.prettify()}
                          </Abbreviation>
                        </div>
                        <div className="td flex flex-row items-baseline absolute top-4 right-4 md:relative md:top-0 md:right-0">
                          <span className="text-style-label mr-1 md:hidden">Collateral Ratio</span>
                          {(collateralRatio => {
                            const crStyle = getCollateralRatioStyle(collateralRatio, recoveryMode)
                            return (
                              <span
                                className={crStyle}
                              >
                                {new Percent(collateralRatio).prettify()}
                              </span>
                            )
                          })(trove.collateralRatio(price))}
                        </div>
                        <div className="td mt-3 md:mt-0">
                          <Transaction
                            id={`liquidate-${trove.ownerAddress}`}
                            tooltip="Liquidate"
                            requires={[
                              liquidatable,
                            ]}
                            send={liquity.send.liquidate.bind(liquity.send, assetAddress, trove.ownerAddress)}
                          >
                            <button className={`text-style-sub ${isLiquidatable ? 'text-color-danger' : ''} border-card p-3 w-full md:border-0 md:w-fit md:p-0`}>Liquidate</button>
                          </Transaction>
                        </div>
                      </div>
                    )
                  }
                )}
              </div>
            </div>
          </div>
        )}

        {/* {loading && <LoadingOverlay />} */}
      </div>
      <div className="flex flex-row items-center justify-end text-style-sub">
        {numberOfTroves !== 0 && (
          <>
            <Abbreviation
              short={`page ${clampedPage + 1} / ${numberOfPages}`}
              sx={{ mr: [0, 3], fontWeight: "body", fontSize: [1, 2], letterSpacing: [-1, 0] }}
            >
              {clampedPage * pageSize + 1}-{Math.min((clampedPage + 1) * pageSize, numberOfTroves)}{" "}
              of {numberOfTroves}
            </Abbreviation>

            <button className="w-6 h-6 ml-1 btn-arrow" onClick={previousPage} disabled={clampedPage <= 0}>
              <Icon name="chevron-left" size="sm" />
            </button>

            <button
              className="w-6 h-6 ml-2 btn-arrow"
              onClick={nextPage}
              disabled={clampedPage >= numberOfPages - 1}
            >
              <Icon name="chevron-right" size="sm" />
            </button>
          </>
        )}

      </div>
    </>
  );
};
